import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cartCount, setCartCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Load cart count from localStorage
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      const cartItems = JSON.parse(savedCart);
      setCartCount(cartItems.length);
    }

    // Check authentication status
    const authToken = localStorage.getItem('authToken');
    setIsLoggedIn(!!authToken);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/product-catalog-category-browse?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setIsSearchOpen(false);
    }
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAuthClick = () => {
    if (isLoggedIn) {
      localStorage.removeItem('authToken');
      setIsLoggedIn(false);
    } else {
      navigate('/user-authentication-login-register');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };

  const isCheckoutPage = location.pathname === '/checkout-process';

  return (
    <header className="fixed top-0 left-0 right-0 bg-surface border-b border-border z-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link to="/homepage-dashboard" className="flex items-center space-x-2 flex-shrink-0">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="ShoppingCart" size={20} color="white" />
            </div>
            <span className="font-heading font-heading-bold text-xl text-primary hidden sm:block">
              FreshCart
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8 flex-1 max-w-2xl mx-8">
            {!isCheckoutPage && (
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Input
                    type="search"
                    placeholder="Search for products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-input focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <Icon
                    name="Search"
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
                  />
                </div>
              </form>
            )}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {!isCheckoutPage && (
              <>
                <Button
                  variant="ghost"
                  onClick={handleCartClick}
                  className="relative p-2"
                  iconName="ShoppingCart"
                  iconSize={24}
                >
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-data font-data-medium rounded-full w-5 h-5 flex items-center justify-center">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </Button>
                <div className="w-px h-6 bg-border"></div>
              </>
            )}
            <Button
              variant="ghost"
              onClick={handleAuthClick}
              iconName={isLoggedIn ? "LogOut" : "User"}
              iconSize={20}
            >
              {isLoggedIn ? 'Logout' : 'Login'}
            </Button>
          </div>

          {/* Mobile Actions */}
          <div className="flex lg:hidden items-center space-x-2">
            {!isCheckoutPage && (
              <>
                <Button
                  variant="ghost"
                  onClick={toggleSearch}
                  className="p-2"
                  iconName="Search"
                  iconSize={20}
                />
                <Button
                  variant="ghost"
                  onClick={handleCartClick}
                  className="relative p-2"
                  iconName="ShoppingCart"
                  iconSize={20}
                >
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs font-data font-data-medium rounded-full w-4 h-4 flex items-center justify-center">
                      {cartCount > 9 ? '9+' : cartCount}
                    </span>
                  )}
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              onClick={toggleMenu}
              className="p-2"
              iconName="Menu"
              iconSize={20}
            />
          </div>
        </div>

        {/* Mobile Search */}
        {isSearchOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-slide-down">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Input
                  type="search"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-input focus:ring-2 focus:ring-primary focus:border-transparent"
                  autoFocus
                />
                <Icon
                  name="Search"
                  size={20}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
                />
              </div>
            </form>
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-slide-down">
            <nav className="space-y-2">
              <Link
                to="/homepage-dashboard"
                className="block px-3 py-2 text-text-primary hover:bg-background rounded-input transition-smooth"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="flex items-center space-x-3">
                  <Icon name="Home" size={20} />
                  <span>Home</span>
                </div>
              </Link>
              <Link
                to="/product-catalog-category-browse"
                className="block px-3 py-2 text-text-primary hover:bg-background rounded-input transition-smooth"
                onClick={() => setIsMenuOpen(false)}
              >
                <div className="flex items-center space-x-3">
                  <Icon name="Grid3X3" size={20} />
                  <span>Browse Products</span>
                </div>
              </Link>
              <button
                onClick={() => {
                  handleAuthClick();
                  setIsMenuOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-text-primary hover:bg-background rounded-input transition-smooth"
              >
                <div className="flex items-center space-x-3">
                  <Icon name={isLoggedIn ? "LogOut" : "User"} size={20} />
                  <span>{isLoggedIn ? 'Logout' : 'My Account'}</span>
                </div>
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;